import type { KyInstance } from 'ky';

import { ensureError, omit } from '@enterprise/core';
import type { OperationResultWithData, PagedData } from '@enterprise/types';

import { Uri } from '#constants/uri';

import type { PagedRecipientQuery } from './types/paged-recipient-query.type';
import type { PagedRecipient } from './types/paged-recipient.type';

/**
 * Fetches a paged list of recipients based on the provided query parameters.
 *
 * @param instance - The Ky HTTP client instance used to make the request.
 * @param query - The query parameters for filtering, sorting, and paging recipients.
 * @returns A promise resolving to an operation result containing paged recipient data.
 * @throws Throws an error if the request fails.
 */
export const getPagedRecipients = async (instance: KyInstance, query: PagedRecipientQuery) => {
  try {
    const { isClosed = false, isExternal = false, ...searchParams } = omit(query, 'filter', 'sort');

    return await instance
      .get<OperationResultWithData<PagedData<PagedRecipient>>>(Uri.recipient.getPaged, {
        searchParams: {
          ...searchParams,
          isClosed,
          isExternal,
        },
      })
      .json();
  } catch (e) {
    const error = ensureError(e);

    throw error;
  }
};
